import { useMutation } from "react-query";

import { fetchClient } from "@/lib/fetchClient";
import type { MutationConfig } from "@/lib/react-query";
import { queryClient } from "@/lib/react-query";
import { isObject } from "../../../utils/typeGuard";
import type { Workflow } from "../types";

export type CreateWorkflowDTO = {
  data: {
    workflow_name: string;
    workflow_type: "ocr" | "sorting";
  };
};

async function createWorkflow({ data }: CreateWorkflowDTO) {
  const { data: res } = await fetchClient.POST("/workflows", {
    body: data,
  });
  if (res === undefined) {
    throw new Error("Failed to create workflow");
  }
  return res;
}

type UseCreateWorkflowOptions = {
  config?: MutationConfig<typeof createWorkflow>;
};

export const useCreateWorkflow = ({
  config,
}: UseCreateWorkflowOptions = {}) => {
  return useMutation({
    onMutate: async (newWorkflow) => {
      await queryClient.cancelQueries("Workflows");

      const previousWorkflows =
        queryClient.getQueryData<Workflow[]>("Workflows");

      queryClient.setQueryData("Workflows", [
        ...(previousWorkflows || []),
        newWorkflow.data,
      ]);

      return { previousWorkflows };
    },
    onError: (_, __, context: unknown) => {
      if (isObject(context) && "previousWorkflows" in context) {
        queryClient.setQueryData("Workflows", context["previousWorkflows"]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries("Workflows");
    },
    ...config,
    mutationFn: createWorkflow,
  });
};
